import {
  Box,
  MenuItem,
  IconButton,
  Tooltip,
  Menu,
  Typography,
  Dialog
} from '@mui/material';
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import CustomDataGrid from '../../../../../../common/components/customized/customdatagrid/CustomDataGrid';
import { useAppDispatch, useAppSelector } from '../../../../../../app/redux/hooks';
import {
  capitalizeFirstLetter,
  convertEpochToDateString,
  convertEpochToDateTime,
  convertEpoctoTimeZoneDate,
  EpochToDate
} from '../../../../../../utils/commonFunctions';
import constant from '../../../../../../utils/constants';
import { Icon } from '@iconify/react';
import CustomButton from '../../../../../../common/components/buttons/CustomButton';
import AddTrip from './AddTrip';
import CustomTextField from '../../../../../../common/components/customized/customtextfield/CustomTextField';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useForm } from 'react-hook-form';
import { debounce } from 'lodash';
import {
  autoPlannerDeleteAction,
  GetGrpBookings
} from '../../../../redux/reducer/autoPlannerSlices/autoplanner';
import { GridActionsCellItem } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import ConfirmationPopup from '../../../../../../common/components/confirmationpopup/ConfirmationPopup';

interface Column {
  field: string;
  headerName: string;
  minWidth: number;
  flex: number;
  renderCell?: (params: { row: any }) => any;
  textAlign?: string;
}

interface PaginationModel {
  page: number;
  pageSize: number;
}

interface Props {
  setExpanded: any;
  clearFilter: any;
  setBreadCrumbs: any;
  payloads: any;
  setPageSizes: any;
  pageSizes: number;
  filterPayload: any;
}

const BookingPvt = ({
  setExpanded,
  setPageSizes,
  pageSizes,
  clearFilter,
  setBreadCrumbs,
  payloads,
  filterPayload
}: Props) => {
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [pageNo, setPageNo] = useState(1);
  const [rows, setRows] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isDialog, setIsDialog] = useState('');
  const [pageSize, setPageSize] = useState(pageSizes || 20);
  const [filter, setFilter] = useState('');
  const [rowCount, setRowCount] = useState(0);
  const [view, setView] = useState<boolean>(false);
  const [tripsGroupRows, setGroupTripsRows] = useState<any[]>([]);
  const [tripPayload, setTripPayload] = useState({ pageNo, pageSize });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const tripsData = useAppSelector(state => state.tripGRP);
  const deleteTrip = useAppSelector(state => state.AutoPlannerDeleteTrip);
  const Routes = useAppSelector(state => state.autoPlannerRoutes.data || []);
  const Agents = useAppSelector(state => state.autoPlannerAgent.data || []);
  const { data } = useAppSelector(state => state.auth);
  const profile = useAppSelector(state => state.myProfile.data);

  const handleMenuClick = (row: any) => {
    setSelectedRow(row);
  };

  const handleUpdateTrip = () => {
    handleMenuClose();
    setIsOpen(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const mode = 'PVT';

  const handleDeleteTrip = async () => {
    if (selectedRow) {
      const payload = {
        selectedRow: selectedRow,
        mode: 'PVT',
        pageDetails: { pageNo, pageSize, autoplannerID: payloads.autoplannerID }
      };
      const res = await dispatch(autoPlannerDeleteAction(payload));

      if (res?.meta?.requestStatus === 'fulfilled') {
        setIsDialog('');
      }
    }
  };

  const { control } = useForm();

  const agentNames =
    Agents?.map((value: string) => ({
      id: value,
      label: capitalizeFirstLetter(value)
    })) || [];

  const configuredRoute =
    Routes?.map((value: string) => ({
      id: value,
      label: value
    })) || [];

  const dispatch = useAppDispatch();
  const groupColumns: Column[] = [
    {
      field: 'actions',
      headerName: 'Action',
      minWidth: 150,
      flex: 1,
      type: 'actions',
      maxWidth: 100,
      getActions: (params: any) => {
        const actions = [
          <GridActionsCellItem
            key='edit'
            icon={<Icon icon='ic:baseline-edit' className='menu-icon update' />}
            label={constant.Update}
            onClick={() => {
              handleMenuClick(params.row);
              handleUpdateTrip();
            }}
            showInMenu
          />
        ];

        if (data?.role === 'ROLE_OPERATOR' || data?.role === 'ROLE_AUTOPLANNER_ADMIN') {
          actions.push(
            <GridActionsCellItem
              key='delete'
              icon={
                <Icon
                  icon='ic:outline-delete'
                  className='menu-icon'
                  style={{ color: '#FF4343' }}
                />
              }
              label={constant.Delete}
              onClick={() => {
                handleMenuClick(params.row);
                setIsDialog('Delete');
                handleMenuClose();
              }}
              showInMenu
            />
          );
        }

        return actions;
      }
    },
    {
      field: 'agent_name',
      headerName: 'Agent Name',
      minWidth: 150,
      flex: 1
    },

    {
      field: 'guest_name',
      headerName: 'Guest Name',
      minWidth: 170,
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip title={params.value || ''}>
          <span>{params.value}</span>
        </Tooltip>
      )
    },
    {
      field: 'guestContactNumber',
      headerName: 'Guest Contact Number',
      minWidth: 200,
      flex: 1,
      valueGetter: (params: any) => (params.value ? params.value : '-')
    },

    {
      field: 'totalPassengers',
      headerName: 'Total Passengers',
      minWidth: 150,
      flex: 1
    },
    {
      field: 'startTimestamp',
      headerName: 'Start Date and Time',
      minWidth: 200,
      flex: 1,
      renderCell: (params: any) =>
        params.value ? convertEpoctoTimeZoneDate(params.value, profile.timezone) : '-'
    },
    {
      field: 'from',
      headerName: 'From',
      minWidth: 300,
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip title={params.value || ''}>
          <span>{params.value}</span>
        </Tooltip>
      )
    },
    {
      field: 'to',
      headerName: 'To',
      minWidth: 300,
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip title={params.value || ''}>
          <span>{params.value}</span>
        </Tooltip>
      )
    }
  ].map((item: any) => ({
    ...item,
    sortable: false,
    align: ['actions']?.includes(item?.field) ? 'center' : 'left',
    headerAlign: ['actions']?.includes(item?.field) ? 'center' : 'left'
  }));

  const handleFilterChange = useCallback(
    debounce((event: string) => {
      setFilter(event);
      if (rows) {
        const payloadPVT = {
          ...tripPayload,
          ...filterPayload,
          tripMode: 'PVT',
          search: event
        };
        setTripPayload(payloadPVT);
        dispatch(GetGrpBookings(payloadPVT));
      }
    }),
    [tripPayload, filterPayload]
  );

  const handlePaginationModelChange = (newPaginationModel: PaginationModel) => {
    const { pageSize: newPageSize, page: newPageNo } = newPaginationModel;
    setPageSize(newPageSize);
    setPageNo(newPageNo + 1);
    const newPage = newPageNo + 1;
    const payload = {
      ...tripPayload,
      pageNo: newPage,
      pageSize: newPageSize
    };
    setTripPayload(payload);
  };

  const handleSortModelChange = (model: any) => {
    const payload = {
      ...tripPayload,
      sortBy: model[0]?.sort,
      sortByField: model[0]?.field
    };
    setTripPayload(payload);
  };

  useEffect(() => {
    if (tripPayload) {
      const payloadSIC = {
        ...tripPayload,
        tripMode: 'PVT'
      };
      dispatch(GetGrpBookings(payloadSIC));
    }
  }, [tripPayload]);

  useLayoutEffect(() => {
    if (tripsData?.data?.data !== null && tripsData?.data?.data?.customTours) {
      setRows(tripsData.data.data.customTours);
      setRowCount(tripsData.data.data.count || 0);
    } else {
      setRows([]);
    }
  }, [tripsData?.data]);

  useEffect(() => {
    if (rows?.length > 0) {
      const data = rows?.map((item: any, index: number) => ({
        ...item,
        sno: index + 1,
        id: index + 1,
        mode: 'PVT',
        from: item.from.locationAddress,
        to: item.to.locationAddress,
        // startTimestamp: item.startTimestamp
        //   ? dayjs
        //       .unix(item.startTimestamp)
        //       .tz(profile?.timeZone)
        //       .format('DD-MM-YYYY HH:mm A')
        //   : '-',
        startTimestamp: item.startTimestamp,
        endTimestamp: item.endTimestamp
          ? convertEpochToDateString(item.endTimestamp)
          : '-'
      }));
      setGroupTripsRows(data);
    } else setGroupTripsRows([]);
  }, [rows]);

  useEffect(() => {
    setBreadCrumbs({
      setViews: setView,
      views: view,
      setPageNo: setPageNo,
      setPageSize: setPageSize
    });
  }, [view, setPageNo, setPageSize]);

  useEffect(() => {
    const payload = {
      ...tripPayload,
      ...payloads
    };
    setTripPayload(payload);
  }, [payloads]);

  useEffect(() => {
    if (pageSize) setPageSizes(pageSize);
  }, [pageSize]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          width: '100%'
        }}
      >
        <Box sx={{ width: 200 }}>
          <CustomTextField
            name='search'
            control={control}
            id='filter-input'
            placeholder='Search...'
            value={filter}
            variant='standard'
            icon={<SearchOutlinedIcon color='primary' />}
            onChangeCallback={(e: any) => handleFilterChange(e)}
          />
        </Box>
      </Box>
      <CustomDataGrid
        rows={tripsGroupRows ?? []}
        sortingMode='server'
        columns={groupColumns}
        onRowClick={(event: any, row) => {
          handleUpdateTrip();
          setSelectedRow(row);
        }}
        loading={tripsData?.isLoading}
        onSortModelChange={handleSortModelChange}
        rowCount={rowCount}
        type='logistics'
        onPaginationModelChange={handlePaginationModelChange}
        pageNo={pageNo}
        pageSize={pageSize}
        paginationModel={{
          page: pageNo ? pageNo - 1 : 0,
          pageSize: pageSize ? pageSize : 20
        }}
      />

      <Dialog open={isDialog === 'Delete'}>
        <Box
          sx={{
            textAlign: 'center',
            padding: '5%',
            minWidth: '350px'
          }}
        >
          <Typography>{constant.DeleteTrip}</Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              paddingTop: '5%',
              justifyContent: 'center'
            }}
          >
            <Box sx={{ marginRight: '12px' }}>
              <CustomButton
                className='saveChanges'
                category='Yes'
                loading={deleteTrip?.isLoading}
                onClick={() => {
                  handleDeleteTrip();
                  // setIsDialog('');
                }}
              />
            </Box>
            <CustomButton
              className='cancel'
              category='No'
              onClick={() => setIsDialog('')}
            />
          </Box>
        </Box>
      </Dialog>
      {/* {isDialog === 'Delete' && (
        <ConfirmationPopup
          open={isDialog === 'Delete'}
          onClose={() => setIsDialog('')}
          loading={deleteTrip?.isLoading}
          onConfirm={() => {
            handleDeleteTrip();
          }}
          title='Delete Booking'
          messages={[
            'This booking includes scheduled entries.',
            'Deleting it will also remove all associated schedules.'
          ]}
          confirmText='Yes, Delete'
          cancelText='No'
        />
      )} */}
      {isOpen && (
        <AddTrip
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          selectedRow={selectedRow}
          pageDetails={tripPayload}
          filterPayload={filterPayload}
          Agents={agentNames}
          Routes={configuredRoute}
          mode={mode}
        />
      )}
    </Box>
  );
};

export default BookingPvt;
